import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Search, Send, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DMThread {
  id: string;
  name: string;
  initials: string;
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
}

const mockThreads: DMThread[] = [
  { id: '1', name: 'Rahul Sharma', initials: 'RS', lastMessage: 'Can you check ticket #1023?', time: '2 min ago', unread: 3, online: true },
  { id: '2', name: 'Priya Mehta', initials: 'PM', lastMessage: 'Thanks, issue resolved!', time: '15 min ago', unread: 0, online: true },
  { id: '3', name: 'Amit Kumar', initials: 'AK', lastMessage: 'Meeting at 3pm today', time: '1 hr ago', unread: 1, online: false },
  { id: '4', name: 'Sneha Joshi', initials: 'SJ', lastMessage: 'Please escalate this ASAP', time: '2 hrs ago', unread: 0, online: false },
  { id: '5', name: 'Vikram Singh', initials: 'VS', lastMessage: 'Updated the SLA config', time: 'Yesterday', unread: 0, online: true },
];

export function AdminChatDM() {
  const [search, setSearch] = useState('');
  const [activeThread, setActiveThread] = useState<DMThread | null>(null);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<{ id: string; from: 'me' | 'them'; text: string; time: string }[]>([
    { id: '1', from: 'them', text: 'Hey, can you check ticket #1023?', time: '10:00 AM' },
    { id: '2', from: 'me', text: 'Sure, looking into it now.', time: '10:02 AM' },
  ]);

  const filtered = mockThreads.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  const sendMessage = () => {
    if (!message.trim()) return;
    setChatMessages(prev => [...prev, {
      id: `m-${Date.now()}`,
      from: 'me',
      text: message,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }]);
    setMessage('');
  };

  return (
    <div className="flex h-full gap-0 overflow-hidden">
      {/* Thread List */}
      <div className="flex w-72 shrink-0 flex-col border-r bg-card">
        <div className="flex items-center gap-2 border-b p-4">
          <MessageCircle className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Direct Messages</h2>
        </div>
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 text-sm"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(thread => (
            <button
              key={thread.id}
              type="button"
              onClick={() => setActiveThread(thread)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                activeThread?.id === thread.id && "bg-muted"
              )}
            >
              <div className="relative shrink-0">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {thread.initials}
                </div>
                {thread.online && (
                  <Circle className="absolute bottom-0 right-0 h-3 w-3 fill-green-500 text-green-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="truncate text-sm font-medium">{thread.name}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{thread.time}</span>
                </div>
                <p className="truncate text-xs text-muted-foreground">{thread.lastMessage}</p>
              </div>
              {thread.unread > 0 && (
                <Badge className="h-5 min-w-5 shrink-0 px-1.5 text-[10px]">{thread.unread}</Badge>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      {activeThread ? (
        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-3 border-b bg-card px-4 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {activeThread.initials}
            </div>
            <div>
              <p className="text-sm font-semibold">{activeThread.name}</p>
              <p className="text-xs text-muted-foreground">{activeThread.online ? 'Online' : 'Offline'}</p>
            </div>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {chatMessages.map(msg => (
              <div key={msg.id} className={cn("flex", msg.from === 'me' ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-xs rounded-2xl px-4 py-2 text-sm",
                  msg.from === 'me'
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                )}>
                  <p>{msg.text}</p>
                  <p className={cn("mt-1 text-[10px]", msg.from === 'me' ? "text-primary-foreground/70" : "text-muted-foreground")}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 border-t bg-card p-3">
            <Input
              placeholder="Type a message..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              className="flex-1"
            />
            <Button size="icon" onClick={sendMessage}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <div className="text-center">
            <MessageCircle className="mx-auto mb-3 h-10 w-10 opacity-30" />
            <p className="text-sm">Select a conversation to start chatting</p>
          </div>
        </div>
      )}
    </div>
  );
}
